import styled from "styled-components";
import React, { Fragment, useEffect, useContext, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uiReducers } from "reduxModules/ducks/ui";
import ToolContext, { ToolContextType } from 'components/services/ToolContext';
import ElementsIndex from "components/elements";
import SpeakerTargetMarkers from "components/elements/SpeakerTargetMarkers";
import MuiExpansionBox from "components/elements/MuiExpansionBox";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { every, isEmpty, values } from "lodash";
import { getAreas, getOuterBox } from "reduxModules/selectors/containerSelectors";
import { AnyAction } from "@reduxjs/toolkit";
import type { RootState } from 'types/redux/state';

/**
 * Interface for DrawManager props
 */
interface DrawManagerProps {
  container: {
    width: number;
    height: number;
  };
}

/**
 * Interface for BoundingBox
 */
interface BoundingBox {
  width: number;
  height: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  cx: number;
  cy: number;
}

/**
 * Interface for DrawingBox
 */
interface DrawingBox {
  bbox: BoundingBox;
  scale: number;
  zoom: boolean;
}

/**
 * Interface for Area
 */
interface Area {
  id: string;
  dimensions?: {
    length: number;
    width: number;
  };
  [key: string]: any;
}

/**
 * Interface for Container
 */
interface Container {
  dimensions: {
    length: number;
    width: number;
  };
  areas: Record<string, Area>;
  [key: string]: any;
}

/**
 * Interface for Element
 */
interface Element {
  element: string;
  [key: string]: any;
}

/**
 * Interface for styled Group props
 */
interface GroupProps {
  id: string;
  children?: ReactNode;
}

/**
 * Styled SVG group component
 */
const Group = styled.g.attrs<GroupProps>(props => ({
  id: props.id,
}))``;

/**
 * DrawManager component
 * 
 * This component manages the drawing of SVG elements based on app state
 * 
 * @param props - Component props
 * @returns SVG elements to render
 */
const DrawManager: React.FC<DrawManagerProps> = ({ container }) => {
  // Get Redux state via selectors
  const { bbox, zoom } = useSelector((state: any) => getDrawingBox(state)) as DrawingBox;
  const activeTab = useSelector((state: any) => state.container.activeTab) as string;
  const { length, width } = useSelector((state: any) => getOuterBox(state)) as { length: number; width: number };
  const dispatch = useDispatch();

  // Set the drawing bounding box and scale
  useEffect(() => {
    // Check if container dimensions are valid
    if (!container.width || !container.height || !length || !width) {
      return;
    }
    
    // Smaller margins to maximize floorplan size
    const leftMargin = 50; // Reduced space for dimension label
    const rightMargin = 50; // Reduced space for dimension label  
    const topMargin = 50; // Reduced space for dimension label
    const bottomMargin = 70; // Reduced space for dimension label and buttons
    
    const availableWidth = container.width - leftMargin - rightMargin;
    const availableHeight = container.height - topMargin - bottomMargin;
    
    // For a 20ft x 14ft room (6.096m x 4.2672m), we want it to fill the screen
    // Calculate pixels per meter to make the room fill the available space
    const pixelsPerMeterX = availableWidth / length;
    const pixelsPerMeterY = availableHeight / width;
    const scale = Math.min(pixelsPerMeterX, pixelsPerMeterY) * 0.90; // Use 90% of available space
    
    const pixelWidth = length * scale;
    const pixelHeight = width * scale;
    
    // Center the drawing area within the available space
    const x0 = leftMargin + (availableWidth - pixelWidth) / 2;
    const y0 = topMargin + (availableHeight - pixelHeight) / 2;
    
    if (scale && scale > 0) {
      dispatch(uiReducers.setScale(scale) as unknown as AnyAction);
      dispatch(uiReducers.setBoundingBox({
        width: pixelWidth,
        height: pixelHeight,
        x0: x0,
        y0: y0,
        x1: x0 + pixelWidth,
        y1: y0 + pixelHeight,
        cx: pixelWidth / 2 + x0,
        cy: pixelHeight / 2 + y0,
      }) as unknown as AnyAction);
    }
  }, [length, width, container, dispatch]);

  // Use the proper ToolContextType
  const toolContext = useContext(ToolContext) as ToolContextType;
  // Use specific selectors instead of getting the entire state
  const containerData = useSelector((state: any) => state.container);
  const currentArea = useSelector((state: any) => state.container.areas[activeTab]);

  // Check if the SVG bounding box is defined before proceeding
  const ready = !isEmpty(bbox) && every(values(bbox));

  // Cast getConditionResult to any to work around TypeScript signature mismatch
  const getConditionResultFn = getConditionResult as any;
  
  const showContainer = useSelector((state: any) => 
    getConditionResultFn(state, { ...toolContext.containerCondition })
  );
  
  const showArea = useSelector((state: any) => 
    getConditionResultFn(state, { ...toolContext.areaCondition })
  );

  // Get areas from state
  const areaArray = values(useSelector((state: any) => getAreas(state))) as Area[];
  

  if (ready) {
    // Reorder the areas to place active on top i.e., draw last
    const index = areaArray.findIndex(x => x.id === activeTab);
    if (areaArray.length >= 1) areaArray.splice(areaArray.length - 1, 0, areaArray.splice(index, 1)[0]);

    return (
      <Fragment>
        {showContainer && !zoom && (toolContext.containerElements ?? []).map((element: Element, index: number) => {
          // Cast ElementsIndex to any to avoid TypeScript errors with dynamic components
          const Element = (ElementsIndex as any)[element.element];
          if (!Element) {
            console.warn(`Element component not found in ElementsIndex: ${element.element}`);
            return null;
          }
          return (
            <Element key={`${element.element}-${index}`} {...element}>{containerData}</Element>
          );
        })}

        {showContainer && (showArea ? [currentArea].filter(Boolean) : areaArray).map((area: Area) => (
          <Group key={area.id} id={area.id}>
            {(toolContext.areaElements ?? []).filter((element: Element) => 
              !['speakers', 'microphones', 'cameras'].includes(element.element)
            ).map((element: Element, index: number) => {
              // Cast ElementsIndex to any to avoid TypeScript errors with dynamic components
              const Element = (ElementsIndex as any)[element.element];
              if (!Element) {
                console.warn(`Element component not found in ElementsIndex: ${element.element}`);
                return null;
              }
              return (
                <Element key={`${element.element}-${index}`} {...element}>{area}</Element>
              );
            })}
          </Group>
        ))}

        {/* Render other elements (non-HeatMapManager) */}
        {(toolContext.otherElements ?? []).filter((element: Element) => element.element !== 'HeatMapManager' && element.element !== 'heatMapManager').map((element: Element, index: number) => {
          // Cast ElementsIndex to any to avoid TypeScript errors with dynamic components
          const Element = (ElementsIndex as any)[element.element];
          if (!Element) {
            console.warn(`Element component not found in ElementsIndex: ${element.element}`);
            return null;
          }
          return (
            <Element key={`${element.element}-${index}`} />
          );
        })}

        {/* Render device elements LAST (top layer) */}
        {showContainer && (showArea ? [currentArea].filter(Boolean) : areaArray).map((area: Area) => {
          const deviceElements = (toolContext.areaElements ?? []).filter((element: Element) => 
            ['speakers', 'microphones', 'cameras'].includes(element.element)
          );
          
          return (
            <Group key={`${area.id}-devices`} id={`${area.id}-devices`}>
              {deviceElements.map((element: Element, index: number) => {
                // Cast ElementsIndex to any to avoid TypeScript errors with dynamic components
                const Element = (ElementsIndex as any)[element.element];
                if (!Element) {
                  console.warn(`Element component not found in ElementsIndex: ${element.element}`);
                  return null;
                }
                return (
                  <Element key={`${element.element}-${index}`} {...element}>{area}</Element>
                );
              })}
              {/* Render speaker target markers on top of devices */}
              <SpeakerTargetMarkers
                key={`${area.id}-speaker-targets`}
                id={area.id}
                origin={{ left: area.location?.left || 0, top: area.location?.top || 0 }}
              />
            </Group>
          );
        })}

        {/* Debug: dashed outlines around the bar + expansion coverage boxes (ceiling/table mic,
            ceiling/pendant speaker). Reads the active-tab area itself, so it's rendered once here
            rather than per-area. Gated internally by the `debugCoverageBoxes` flag in
            constants.js — flip that flag to turn the dotted lines on/off, no code change needed. */}
        {showContainer && <MuiExpansionBox />}
      </Fragment>
    );
  }

  return null;
};

export default DrawManager;
